/**
 * useCredits Hook
 * Manages fetching and tracking user credit balance
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getCredits } from '@/services/credits';
import { useAuth } from '@/context';

const CREDITS_LOADING_TIMEOUT_MS = 10000;

interface UseCreditsReturn {
  /** Current credit balance */
  credits: number;
  /** Loading state */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Manually refetch credits (call after transactions) */
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage user's credit balance
 *
 * @returns Credit balance, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { credits, loading, refetch } = useCredits();
 *
 * // After a transaction
 * await deductCredits(userId, 1, 'ai_generation');
 * await refetch(); // Update displayed balance
 * ```
 */
export function useCredits(): UseCreditsReturn {
  const { user } = useAuth();
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCredits = useCallback(async () => {
    if (!user) {
      setCredits(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const balance = await getCredits(user.id);
      setCredits(balance);
    } catch (err) {
      console.error('[useCredits] Failed to fetch credits:', err);
      setError(err instanceof Error ? err.message : 'Failed to load credits');
      setCredits(0);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Safety timeout: if credits loading hangs, stop showing loading state
  const loadingResolvedRef = useRef(false);
  useEffect(() => {
    if (!loading) {
      loadingResolvedRef.current = true;
      return;
    }

    loadingResolvedRef.current = false;
    const timeout = setTimeout(() => {
      if (!loadingResolvedRef.current) {
        console.warn('[useCredits] Credits loading timed out after 10s — defaulting to 0');
        setLoading(false);
        setCredits(0);
      }
    }, CREDITS_LOADING_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [loading]);

  return {
    credits,
    loading,
    error,
    refetch: fetchCredits,
  };
}
